/**
 * LatePaymentsDisputeModal
 * ────────────────────────────────────────────────────────────────────────────
 * 2026-02-28 (user request) — When the user checks the "Late Payments Dispute
 * Letter" tile in the parent DisputeLetterModal, we open THIS specialised
 * entry form.  It captures the structured data the aggressive FCRA template
 * needs — creditor name, optional account last-four, optional date opened,
 * a list of disputed marks (each = month + year + 30/60/90/120/150-day
 * severity), and an optional evidence screenshot of the tradeline showing
 * the late marks.
 *
 * SIGNATURE / HANDWRITING FLOW — this modal does NOT render its own
 * signature-font / handwriting-style picker.  Those choices are driven by
 * the parent dispute-letter flow (DisputeLetterModal) and flow in via the
 * `style`, `signatureFont`, `bodyFont`, `handwritingStyle`,
 * `handwritingFinish` props so late-payment letters go through the EXACT
 * same signature/font pipeline every other dispute letter uses.
 *
 * On Generate we POST to /api/dispute-letter/late-payments-aggressive and
 * hand the resulting PDF blob to the parent via `onGenerated(blob, filename)`.
 */

import React, { useState, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { X, Plus, Trash2, Upload, Loader2, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Restricted to what the FCRA template addresses.  30 = the first
// reportable delinquency; 150 = pre-charge-off/pre-collection.
const SEVERITIES = [30, 60, 90, 120, 150];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 8 }, (_, i) => currentYear - i);

const emptyMark = () => ({
  id: Math.random().toString(36).slice(2),
  month: '',
  year: currentYear,
  severity: 30,
});

export default function LatePaymentsDisputeModal({
  open,
  onClose,
  clientData,          // {name, address, city, state, zip_code}
  signatureData,       // optional base64 signature already collected upstream
  onGenerated,         // (pdfBlob, filename, entry) → void
  // 2026-02-28 (user request) — signature/font/handwriting choices flow in
  // from the parent dispute-letter flow so late-payment letters go through
  // the SAME signature pipeline every other letter uses.  No duplicate
  // picker lives inside this modal.
  style = 'handwritten',                 // 'typed' | 'handwritten'
  signatureFont = 'cursive_elegant',
  bodyFont = 'times',
  handwritingStyle = 'casual',
  handwritingTool = 'pen',
  handwritingPaper = 'white',
  handwritingFinish = 'original',
  // 2026-02-28 (user request) — accept a prefill payload from the
  // AI Extractor so every section lands populated.  Shape:
  //   {
  //     creditor_name: "CAP 1",
  //     creditor_address: "PO Box 30285",
  //     creditor_city_state_zip: "Salt Lake City, UT 84130",
  //     account_last4: "1234",
  //     date_opened: "07-22-2019",
  //     balance: 5825,
  //     marks: [{month:"03", year:2024, severity:30}, ...],
  //     screenshot_base64: "data:image/..." | null,
  //   }
  prefill = null,
}) {
  const [creditorName, setCreditorName] = useState(prefill?.creditor_name || '');
  const [creditorAddress, setCreditorAddress] = useState(prefill?.creditor_address || '');
  const [creditorCityStateZip, setCreditorCityStateZip] = useState(prefill?.creditor_city_state_zip || '');
  const [accountLast4, setAccountLast4] = useState(prefill?.account_last4 || '');
  // 2026-02-28 — surface date_opened + balance as editable fields,
  // pre-filled from the AI extractor so the admin's typing load drops
  // to zero when the screenshot already carried the data.
  const [dateOpened, setDateOpened] = useState(
    prefill?.date_opened || prefill?._hint_date_opened || ''
  );
  const [balance, setBalance] = useState(
    prefill?.balance != null
      ? String(prefill.balance)
      : prefill?._hint_balance != null
        ? String(prefill._hint_balance)
        : ''
  );
  const _prefilledMarks = useMemo(() => {
    const m = prefill?.marks;
    if (!Array.isArray(m) || !m.length) return [emptyMark()];
    const MONTH_NAMES = ['January','February','March','April','May','June',
                         'July','August','September','October','November','December'];
    return m.map((x) => {
      // Accept both numeric ("03", 3) and name ("March") month inputs
      let monthLabel = '';
      const raw = x.month;
      if (typeof raw === 'number' || /^\d+$/.test(String(raw || ''))) {
        const idx = Number(raw) - 1;
        if (idx >= 0 && idx < 12) monthLabel = MONTH_NAMES[idx];
      } else if (typeof raw === 'string') {
        const match = MONTH_NAMES.find((n) => n.toLowerCase() === raw.toLowerCase());
        monthLabel = match || '';
      }
      return {
        id: `mark-${Math.random().toString(36).slice(2, 10)}`,
        month: monthLabel,
        year: Number(x.year) || '',
        severity: Number(x.severity) || '',
      };
    });
  }, [prefill?.marks]);
  const [marks, setMarks] = useState(_prefilledMarks);
  const [screenshotDataUrl, setScreenshotDataUrl] = useState(prefill?.screenshot_base64 || null);
  const [generating, setGenerating] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  if (!open) return null;

  const updateMark = (id, patch) => {
    setMarks((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };
  const addMark = () => setMarks((prev) => [...prev, emptyMark()]);
  const removeMark = (id) => setMarks((prev) => prev.filter((m) => m.id !== id));

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please drop an image file (PNG / JPG / HEIC).');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setScreenshotDataUrl(e.target.result);
    reader.onerror = () => toast.error('Failed to read the image file.');
    reader.readAsDataURL(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer?.files?.[0];
    handleFile(f);
  };

  const generate = async () => {
    if (!creditorName.trim()) {
      toast.error('Please enter the creditor name (or drop a screenshot showing it).');
      return;
    }
    const validMarks = marks
      .filter((m) => m.month && m.year && m.severity)
      .map((m) => ({ month: m.month, year: Number(m.year), severity: Number(m.severity) }));
    if (validMarks.length === 0) {
      toast.error('Add at least one disputed late mark (month + year + severity).');
      return;
    }
    setGenerating(true);
    try {
      const _balanceNum = parseFloat(String(balance).replace(/[^0-9.-]/g, ''));
      const payload = {
        entry: {
          creditor_name: creditorName.trim(),
          creditor_address: creditorAddress.trim() || null,
          creditor_city_state_zip: creditorCityStateZip.trim() || null,
          account_last4: accountLast4.trim() || null,
          date_opened: dateOpened.trim() || null,
          balance: Number.isFinite(_balanceNum) ? _balanceNum : null,
          marks: validMarks,
          screenshot_base64: screenshotDataUrl || null,
        },
        client_data: clientData || {},
        signature_data: signatureData || null,
        // 2026-02-28 — signature/handwriting choices inherited from the
        // parent dispute-letter flow (no duplicate picker here).
        style,
        signature_font: signatureFont,
        body_font: bodyFont,
        handwriting_style: handwritingStyle,
        handwriting_tool: handwritingTool,
        handwriting_paper: handwritingPaper,
        handwriting_finish: handwritingFinish,
      };
      const resp = await axios.post(
        `${API}/api/dispute-letter/late-payments-aggressive`,
        payload,
        { responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const filename = `Late_Payments_Dispute_${creditorName.replace(/[^a-zA-Z0-9._-]/g, '_')}.pdf`;
      toast.success('Aggressive late-payments dispute letter generated');
      onGenerated?.(blob, filename, payload.entry);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Failed to generate letter';
      toast.error(`Generate failed: ${detail}`);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/80 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
      data-testid="late-payments-modal"
    >
      <div className="w-full max-w-2xl bg-slate-900 border border-white/10 rounded-2xl shadow-2xl my-6">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-fuchsia-400" />
              Late Payments Dispute — Build the Letter
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              Aggressive FCRA §§ 1681a / 1681i / 1681s-2 demand with an AI-tailored
              opening paragraph. Signature + handwriting flow is inherited from the
              main dispute-letter picker — no separate style controls here.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 text-slate-400"
            data-testid="late-payments-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Screenshot drop zone — user's #1 asked-for feature */}
          <div>
            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
              📎 Drop the tradeline screenshot (optional)
            </label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`cursor-pointer rounded-xl border-2 border-dashed transition ${
                dragActive
                  ? 'border-fuchsia-400 bg-fuchsia-500/10'
                  : screenshotDataUrl
                  ? 'border-emerald-500/50 bg-emerald-500/5'
                  : 'border-slate-600 bg-slate-800/50 hover:border-slate-500'
              } p-4 flex flex-col items-center justify-center gap-2 min-h-[120px]`}
              data-testid="late-payments-dropzone"
            >
              {screenshotDataUrl ? (
                <>
                  <img
                    src={screenshotDataUrl}
                    alt="tradeline screenshot"
                    className="max-h-[180px] rounded-lg border border-white/10"
                  />
                  <p className="text-xs text-emerald-300">
                    ✓ Screenshot attached — click to replace
                  </p>
                </>
              ) : (
                <>
                  <Upload className="w-6 h-6 text-slate-400" />
                  <p className="text-sm text-slate-300">
                    Drag & drop a screenshot showing the late marks
                  </p>
                  <p className="text-[11px] text-slate-500">
                    …or click to browse. PNG / JPG. Skip this if you'll type the info below.
                  </p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="late-payments-file-input"
              />
            </div>
          </div>

          {/* Creditor block */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                Creditor / Furnisher Name *
              </label>
              <input
                value={creditorName}
                onChange={(e) => setCreditorName(e.target.value)}
                placeholder="e.g. American Express, Capital One, Ally Financial"
                className="w-full rounded-lg bg-slate-800 border border-white/10 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-fuchsia-400 focus:outline-none"
                data-testid="late-payments-creditor-name"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                Account Last 4 (optional)
              </label>
              <input
                value={accountLast4}
                onChange={(e) => setAccountLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="1234"
                maxLength={4}
                className="w-full rounded-lg bg-slate-800 border border-white/10 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-fuchsia-400 focus:outline-none font-mono"
                data-testid="late-payments-last4"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                Date Opened (optional)
              </label>
              <input
                value={dateOpened}
                onChange={(e) => setDateOpened(e.target.value)}
                placeholder="07-22-2019"
                className="w-full rounded-lg bg-slate-800 border border-white/10 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-fuchsia-400 focus:outline-none font-mono"
                data-testid="late-payments-date-opened"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                Balance (optional)
              </label>
              <input
                value={balance}
                onChange={(e) => setBalance(e.target.value)}
                placeholder="5825"
                className="w-full rounded-lg bg-slate-800 border border-white/10 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-fuchsia-400 focus:outline-none font-mono"
                data-testid="late-payments-balance"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                Furnisher Address (optional)
              </label>
              <input
                value={creditorAddress}
                onChange={(e) => setCreditorAddress(e.target.value)}
                placeholder="P.O. Box 981537"
                className="w-full rounded-lg bg-slate-800 border border-white/10 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-fuchsia-400 focus:outline-none"
                data-testid="late-payments-address"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                Furnisher City, State ZIP (optional)
              </label>
              <input
                value={creditorCityStateZip}
                onChange={(e) => setCreditorCityStateZip(e.target.value)}
                placeholder="El Paso, TX 79998"
                className="w-full rounded-lg bg-slate-800 border border-white/10 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-fuchsia-400 focus:outline-none"
                data-testid="late-payments-csz"
              />
            </div>
          </div>

          {/* Marks list */}
          <div>
            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">
              Disputed Late Marks *
            </label>
            <div className="space-y-2">
              {marks.map((m, idx) => (
                <div
                  key={m.id}
                  className="grid grid-cols-12 gap-2 items-center bg-slate-800/50 rounded-lg p-2 border border-white/5"
                  data-testid={`late-mark-row-${idx}`}
                >
                  <select
                    value={m.month}
                    onChange={(e) => updateMark(m.id, { month: e.target.value })}
                    className="col-span-5 rounded-md bg-slate-900 border border-white/10 px-2 py-1.5 text-sm text-white"
                    data-testid={`late-mark-month-${idx}`}
                  >
                    <option value="">Month…</option>
                    {MONTHS.map((mo) => (
                      <option key={mo} value={mo}>{mo}</option>
                    ))}
                  </select>
                  <select
                    value={m.year}
                    onChange={(e) => updateMark(m.id, { year: Number(e.target.value) })}
                    className="col-span-3 rounded-md bg-slate-900 border border-white/10 px-2 py-1.5 text-sm text-white"
                    data-testid={`late-mark-year-${idx}`}
                  >
                    {YEARS.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                  <select
                    value={m.severity}
                    onChange={(e) => updateMark(m.id, { severity: Number(e.target.value) })}
                    className="col-span-3 rounded-md bg-slate-900 border border-white/10 px-2 py-1.5 text-sm text-white"
                    data-testid={`late-mark-sev-${idx}`}
                  >
                    {SEVERITIES.map((s) => (
                      <option key={s} value={s}>{s} days</option>
                    ))}
                  </select>
                  <button
                    onClick={() => removeMark(m.id)}
                    disabled={marks.length === 1}
                    className="col-span-1 p-1.5 rounded hover:bg-red-500/20 text-red-400 disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Remove mark"
                    data-testid={`late-mark-remove-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={addMark}
              className="mt-2 text-xs text-fuchsia-300 hover:text-fuchsia-200 flex items-center gap-1"
              data-testid="late-payments-add-mark"
            >
              <Plus className="w-3.5 h-3.5" /> Add another late mark
            </button>
          </div>

          {/* Inherited style summary — tiny read-only strip so the admin
              knows which signature/handwriting flow this letter will go
              through, matching every other dispute letter. */}
          <div
            className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-[11px] text-slate-400 flex flex-wrap gap-x-4 gap-y-1"
            data-testid="late-payments-inherited-style"
          >
            <span>
              Signature flow:{' '}
              <span className="text-slate-200 font-medium">
                {style === 'handwritten' ? 'Handwritten (converted)' : 'Typed (clean)'}
              </span>
            </span>
            <span>
              Signature font:{' '}
              <span className="text-slate-200 font-medium">{signatureFont}</span>
            </span>
            <span>
              Body font:{' '}
              <span className="text-slate-200 font-medium">{bodyFont}</span>
            </span>
            {style === 'handwritten' && (
              <span>
                Handwriting:{' '}
                <span className="text-slate-200 font-medium">{handwritingStyle}</span>
              </span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-white/10 bg-slate-900/50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5"
            data-testid="late-payments-cancel"
          >
            Cancel
          </button>
          <button
            onClick={generate}
            disabled={generating}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-fuchsia-600 to-pink-600 hover:from-fuchsia-500 hover:to-pink-500 text-white shadow-lg flex items-center gap-2 disabled:opacity-50"
            data-testid="late-payments-generate"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <FileText className="w-4 h-4" />
                Generate Aggressive Letter
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
